/**
 * Text on a Path — the baked per-character half of the wire format (WMPCM-9542).
 *
 * `<TP>` carries the text twice and both halves are required (see `TextOnPathElementData` in
 * `api/types/stowed-element.ts`): `<TL>` is the logical string plus its typography, and `<XT>`
 * holds one `<PX>` per character with the position, size and rotation that character ended up at
 * along the path. yb360 draws from `<XT>` and never re-flows the text itself, so a missing or
 * mis-indexed `<XT>` is not a cosmetic loss — it is the difference between yb360 rendering the
 * text and rendering nothing.
 *
 * The geometry **can only be read out of the laid-out DOM**. `<textPath>` placement is the
 * browser's own text-on-a-path layout (advance widths, kerning, the per-glyph rotation that makes
 * the text follow a curve); nothing in this codebase reproduces it, and re-deriving it from
 * `getPointAtLength` plus canvas `measureText` would be a second, silently divergent layout
 * engine. So this module reads `SVGTextContentElement`'s own char accessors off the node the
 * renderer already drew.
 *
 * Two conventions are load-bearing:
 *
 *  - **One entry per character, whitespace included.** `<TL>` and `<XT>` are index-aligned with the
 *    string, so skipping spaces would shift every character after the first. This is why the
 *    `<text>` node sets `xml:space="preserve"` (see `TextOnPathText` in `shape-renderer/components/text-on-path-text.tsx`) —
 *    without it SVG collapses whitespace runs and `getNumberOfChars()` reports fewer characters
 *    than the string has.
 *  - **Center-relative coordinates.** Geometry nested inside an element is written relative to the
 *    element's centre on the wire, exactly as `convertPathCommands` does for path points
 *    (`x - width/2`) and matching legacy `page-editor`'s own save code. The DOM reports shape-local
 *    (top-left origin) coordinates, so the centre is subtracted here.
 *
 * Everything is tolerant of a missing or non-measuring node: jsdom implements none of the char
 * accessors, and the renderer's `<text>` is absent whenever the shape is off-screen or the config
 * has no content. A bake with no source still emits one zeroed entry per character rather than an
 * empty array, so the two halves stay index-aligned and yb360 receives a well-formed (if
 * unpositioned) container instead of a truncated one.
 */

import type { TextOnPathCharacter } from '@/api/types/stowed-element'
import type { TextOnPathCharGeometrySource } from './types/text-on-path-char-geometry-source'
import type { MeasureTextOnPathCharactersArgs } from './types/measure-text-on-path-characters-args'
import type { ReadTextOnPathCharactersArgs } from './types/read-text-on-path-characters-args'

/** Attribute `TextOnPathText` stamps on its `<text>` node so a bake can find it by shape id. */
export const TEXT_ON_PATH_TEXT_NODE_ATTR = 'data-text-on-path-shape-id'

/**
 * Narrows an arbitrary node to a usable geometry source.
 *
 * Presence of the four methods is the test, not `instanceof SVGTextContentElement`: jsdom defines
 * the class but not the accessors, so an `instanceof` check would pass and then throw on the first
 * call.
 */
export function asTextOnPathCharGeometrySource(node: unknown): TextOnPathCharGeometrySource | null {
  if (node == null || typeof node !== 'object') return null

  const candidate = node as Partial<TextOnPathCharGeometrySource>

  if (
    typeof candidate.getNumberOfChars !== 'function' ||
    typeof candidate.getStartPositionOfChar !== 'function' ||
    typeof candidate.getExtentOfChar !== 'function' ||
    typeof candidate.getRotationOfChar !== 'function'
  ) {
    return null
  }

  return candidate as TextOnPathCharGeometrySource
}

/** Wire decimals are written at precision 6; round here so the payload carries no float noise. */
function round6(value: number): number {
  return Number.isFinite(value) ? Math.round(value * 1e6) / 1e6 : 0
}

/**
 * Splits `text` into the units `<PX>` entries are counted in.
 *
 * Code POINTS, not code units, so an astral character (an emoji pasted into the text field) writes
 * one entry holding the whole character rather than two entries each holding half a surrogate pair
 * — a lone surrogate is not valid XML and would break the backend's recompose. The DOM's own char
 * index is still a code-UNIT index, so the loop below walks the two in step via `unitIndex`.
 */
function splitCharacters(text: string): string[] {
  return Array.from(text)
}

function emptyCharacter(character: string): TextOnPathCharacter {
  return {
    character,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    rotation: 0,
    baseline: 0,
    textAdvance: 0,
  }
}

function tryGeometry<T>(read: () => T): T | null {
  try {
    return read()
  } catch {
    return null
  }
}

function safeNumberOfChars(source: TextOnPathCharGeometrySource): number {
  const read = tryGeometry(() => source.getNumberOfChars())

  return typeof read === 'number' && Number.isFinite(read) ? read : 0
}

/**
 * One `<PX>` per character of `text`, positioned from the live layout when one is available.
 *
 * A character the source cannot report (index past `getNumberOfChars()`, or an accessor that
 * throws — Chrome raises `IndexSizeError` rather than returning null) falls back to a zeroed entry
 * instead of being dropped, preserving the index alignment described in the module comment.
 */
export function measureTextOnPathCharacters({
  text,
  source,
  width,
  height,
}: MeasureTextOnPathCharactersArgs): TextOnPathCharacter[] {
  const characters = splitCharacters(text)

  if (characters.length === 0) return []

  const centerX = Number.isFinite(width) ? width / 2 : 0
  const centerY = Number.isFinite(height) ? height / 2 : 0
  const measurable = source == null ? 0 : safeNumberOfChars(source)

  const out: TextOnPathCharacter[] = []
  let unitIndex = 0

  for (const character of characters) {
    const index = unitIndex

    unitIndex += character.length

    if (source == null || index >= measurable) {
      out.push(emptyCharacter(character))
      continue
    }

    const start = tryGeometry(() => source.getStartPositionOfChar(index))
    const extent = tryGeometry(() => source.getExtentOfChar(index))
    const rotation = tryGeometry(() => source.getRotationOfChar(index))
    // `TA` is the step to the NEXT character's anchor, which is what yb360 advances by. The last
    // character has no next anchor, so it falls back to its own measured width.
    const next =
      index + character.length < measurable
        ? tryGeometry(() => source.getStartPositionOfChar(index + character.length))
        : null
    const advance =
      next != null && start != null
        ? Math.hypot(next.x - start.x, next.y - start.y)
        : (extent?.width ?? 0)

    out.push({
      character,
      x: round6((start?.x ?? 0) - centerX),
      y: round6((start?.y ?? 0) - centerY),
      width: round6(extent?.width ?? 0),
      height: round6(extent?.height ?? 0),
      rotation: round6(typeof rotation === 'number' ? rotation : 0),
      // `BL` is the character's baseline offset within its own box. `getStartPositionOfChar`
      // already returns the point ON the baseline, so the offset relative to it is 0 — what yb360
      // needs is in `y`, not duplicated here.
      baseline: 0,
      textAdvance: round6(advance),
    })
  }

  return out
}

/** Escapes a shape id for use inside a CSS attribute selector's double-quoted value. */
function cssAttributeValue(value: string): string {
  return value.replace(/["\\]/g, (match) => `\\${match}`)
}

/**
 * Finds the rendered `<text>` node for `shapeId` and bakes its characters.
 *
 * Safe to call from any save path: with no document, no matching node, or a node whose accessors
 * are unavailable, it degrades to the zeroed entries `measureTextOnPathCharacters` produces.
 */
export function readTextOnPathCharacters({
  shapeId,
  text,
  width,
  height,
  root,
}: ReadTextOnPathCharactersArgs): TextOnPathCharacter[] {
  const container = root ?? (typeof document === 'undefined' ? null : document)
  const node =
    container == null
      ? null
      : container.querySelector(`[${TEXT_ON_PATH_TEXT_NODE_ATTR}="${cssAttributeValue(shapeId)}"]`)

  return measureTextOnPathCharacters({
    text,
    source: asTextOnPathCharGeometrySource(node),
    width,
    height,
  })
}
